import React from "react"
import {
  Flex,
  Box,
  styled,
  forwardRef,
  createStyles,
  HTMLStyledProps,
  Text,
} from "@design-system/core"
import { createContext } from "@design-system/react-utils"
import {
  CheckCircleSolidIcon,
  WarningSolidIcon,
  InfoSolidIcon,
  QuestionSolidIcon,
  TagIcon,
  ErrorSolidIcon,
} from "@design-system/icons"
import {
  AlertProps,
  AlertContentProps,
  AlertTitleProps,
  AlertDescriptionProps,
} from "./types"

const STATUSES = {
  info: { icon: InfoSolidIcon },
  success: { icon: CheckCircleSolidIcon },
  error: { icon: ErrorSolidIcon },
  warning: { icon: WarningSolidIcon },
  educational: { icon: QuestionSolidIcon },
  promotional: { icon: TagIcon },
}

export type AlertStatus = keyof typeof STATUSES

interface AlertContext {
  status: AlertStatus
}

const [AlertProvider, useAlertContext] = createContext<AlertContext>({
  name: "AlertContext",
  errorMessage:
    "useAlertContext: `context` is undefined. Seems you forgot to wrap alert components in `<Alert />`",
})

export const Alert = forwardRef<AlertProps, "div">((props, ref) => {
  const { status = "info", quiet = false, ...rest } = props

  return (
    <AlertProvider value={{ status }}>
      <Flex
        role="alert"
        ref={ref}
        {...rest}
        style={[styles.container, !quiet && containerStyles[status]]}
      />
    </AlertProvider>
  )
})

export const AlertContent = forwardRef<AlertContentProps, "div">(
  (props, ref) => {
    return <Box ref={ref} {...props} />
  },
)

export const AlertTitle = forwardRef<AlertTitleProps, "div">((props, ref) => {
  return (
    <Text ref={ref} style={[styles.title]}>
      {props.children}
    </Text>
  )
})

export const AlertDescription = forwardRef<AlertDescriptionProps, "div">(
  (props, ref) => {
    return <Text ref={ref}>{props.children}</Text>
  },
)

export interface AlertIconProps extends HTMLStyledProps<"span"> {}

export const AlertIcon: React.FC<AlertIconProps> = (props) => {
  const { status } = useAlertContext()
  const BaseIcon = STATUSES[status]?.icon

  return (
    <styled.span
      display="inherit"
      {...props}
      style={[styles.icon, iconStyles[status]]}
    >
      <BaseIcon />
    </styled.span>
  )
}

const styles = createStyles((theme) => ({
  container: {
    alignItems: "center",
    borderRadius: theme.borderRadii.medium,
    fontSize: theme.textFontSizes.medium,
    position: "relative",
    padding: theme.spaces.spacing16,
    paddingRight: theme.spaces.spacing48,
  },
  icon: {
    minWidth: theme.spaces.spacing20,
    width: theme.spaces.spacing20,
    marginRight: theme.spaces.spacing12,
  },
  title: {
    fontWeight: theme.fontWeights.bold,
    marginRight: theme.spaces.spacing12,
  },
}))

const containerStyles = createStyles((theme) => ({
  error: {
    backgroundColor: theme.backgroundColors.error,
  },
  success: {
    backgroundColor: theme.backgroundColors.success,
  },
  warning: {
    backgroundColor: theme.backgroundColors.attention,
  },
  info: {
    backgroundColor: theme.backgroundColors.informational,
  },
  educational: {
    backgroundColor: theme.backgroundColors.disabled,
  },
  promotional: {
    backgroundColor: theme.backgroundColors.discount,
  },
}))

const iconStyles = createStyles((theme) => ({
  error: {
    color: theme.textColors.error,
  },
  success: {
    color: theme.textColors.brand,
  },
  warning: {
    color: theme.textColors.warning,
  },
  info: {
    color: theme.textColors.details,
  },
  educational: {
    color: theme.colors.black,
  },
  promotional: {
    color: theme.textColors.brand,
  },
}))
